import {
  coerceBhConnectionCount,
  coerceBhMedicalDiagnosisRows,
  isWarehouseWhoWereReachingSnapshot,
  normalizeBhMedicalDiagnosesSnapshot,
  type BhMedicalDiagnosisRow,
} from '@act/shared/data-transfer-objects';
import SectionReveal from './SectionReveal';
import SectionBanner from './SectionBanner';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import {
  body,
  CORAL,
  display,
  foreground,
  muted,
  SEAGREEN,
} from './tokens';

const dxCardStyle = {
  background: '#fff',
  border: '0.5px solid var(--reema-midnight-25)',
  borderRadius: 24,
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
} as const;

interface BhMedicalDiagnosisTableProps {
  title: string;
  total: number;
  totalColor: string;
  overlineLabel: string;
  rows: BhMedicalDiagnosisRow[];
}

/**
 * Renders a Studio-style diagnosis breakdown card with headline total and ranked rows.
 * @param props - Card title, total styling, and ranked diagnosis rows.
 */
const BhMedicalDiagnosisTable = ({
  title,
  total,
  totalColor,
  overlineLabel,
  rows,
}: BhMedicalDiagnosisTableProps) => (
  <div className="bh-medical-diagnosis-card" style={dxCardStyle}>
    <h3
      className="bh-medical-diagnosis-card__title"
      style={{
        fontFamily: display,
        fontWeight: 600,
        fontSize: 'var(--reema-text-h3)',
        color: foreground,
        margin: 0,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      }}
    >
      {title}
    </h3>
    <div
      className="bh-medical-diagnosis-card__total"
      style={{
        fontFamily: display,
        fontWeight: 600,
        fontSize: 72,
        lineHeight: 1,
        color: totalColor,
        letterSpacing: '-0.02em',
        marginTop: '1rem',
      }}
    >
      {total.toLocaleString()}
    </div>
    <div
      className="bh-medical-diagnosis-card__overline"
      style={{
        fontFamily: body,
        fontWeight: 700,
        fontSize: 'var(--reema-text-overline)',
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        color: muted,
        marginTop: '0.5rem',
      }}
    >
      {overlineLabel}
    </div>
    <div
      className="bh-medical-diagnosis-card__divider"
      style={{
        height: '0.5px',
        background: 'var(--reema-midnight-10)',
        margin: '1.5rem 0',
      }}
    />
    <div className="bh-medical-diagnosis-card__rows flex flex-col gap-3">
      {rows.map(row => (
        <div
          key={row.label}
          className="flex justify-between items-baseline gap-4"
        >
          <span
            style={{
              fontFamily: body,
              fontSize: 'var(--reema-text-body)',
              color: muted,
            }}
          >
            {row.label}
          </span>
          <span
            style={{
              fontFamily: body,
              fontWeight: 600,
              fontSize: 'var(--reema-text-body-lg)',
              color: foreground,
              textAlign: 'right',
            }}
          >
            {row.count.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Behavioral Health and Medical Diagnoses section from warehouse dx breakdowns.
 * @param props - Resolved section snapshot.
 */
const BhMedicalDiagnoses = ({
  data,
  customer,
  reportingPeriod,
}: ReportHostedSectionProps) => {
  const displayData = normalizeBhMedicalDiagnosesSnapshot(
    data,
    customer,
    reportingPeriod,
  );
  const breakdown = coerceBhMedicalDiagnosisRows(displayData.breakdown);
  const medical = coerceBhMedicalDiagnosisRows(displayData.medical);
  const totalNewBh = coerceBhConnectionCount(displayData.totalNewBhDx);
  const totalNewMedical = coerceBhConnectionCount(displayData.totalNewMedicalDx);
  const isWarehouse = isWarehouseWhoWereReachingSnapshot(displayData);

  return (
    <HostedReportSectionLayout
      sectionDomId="bh-medical-diagnoses"
      className="section--white report-hosted-section-body"
      style={{ fontFamily: body }}
      banner={
        <SectionBanner
          title="Behavioral Health and Medical Diagnoses"
          iconHref="#icon_behavioral_health"
        />
      }
    >
      <SectionReveal>
        <ReportMockFieldFrame data={displayData} field="totalNewBhDx" className="mb-10">
          <div
            className="bh-medical-diagnoses-stat-grid stat-grid stat-grid--2"
            style={{ marginBottom: '4rem' }}
          >
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">{totalNewBh.toLocaleString()}</div>
              <div className="stat-label">New behavioral health diagnoses</div>
            </div>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">
                {totalNewMedical.toLocaleString()}
              </div>
              <div className="stat-label">New medical diagnoses</div>
            </div>
          </div>
        </ReportMockFieldFrame>

        <div className="bh-medical-diagnoses-tables-grid grid md:grid-cols-2 gap-6 mb-10">
          <ReportMockFieldFrame data={displayData} field="breakdown">
            {breakdown.length > 0 ? (
              <BhMedicalDiagnosisTable
                title="New behavioral health diagnoses"
                total={totalNewBh > 0 ? totalNewBh : breakdown.reduce((sum, row) => sum + row.count, 0)}
                totalColor={SEAGREEN}
                overlineLabel="TOTAL NEW BH DX"
                rows={breakdown}
              />
            ) : null}
          </ReportMockFieldFrame>
          <ReportMockFieldFrame data={displayData} field="medical">
            {medical.length > 0 ? (
              <BhMedicalDiagnosisTable
                title="New medical diagnoses"
                total={
                  totalNewMedical > 0
                    ? totalNewMedical
                    : medical.reduce((sum, row) => sum + row.count, 0)
                }
                totalColor={CORAL}
                overlineLabel="TOTAL NEW MEDICAL DX"
                rows={medical}
              />
            ) : null}
          </ReportMockFieldFrame>
        </div>

        {!isWarehouse ? (
          <p className="text-sm" style={{ color: muted }}>
            Use Refresh data to load diagnosis breakdowns from the warehouse.
          </p>
        ) : null}
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default BhMedicalDiagnoses;
